import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { Loader, SelectionGroup, CheckboxList, Price } from '@grandvisionhq/common';
import { Heading } from '@grandvisionhq/elements';
import { withIntl } from '@grandvisionhq/state';
import { getAttrValue, isPlainObject, getNestedProperty, getLocalizedValue } from '@grandvisionhq/utils';
import LensPacks from '../LensPacks/LensPacks.js';
import { findAndSortBenefits, calcGlassesPrice, calcGlassesPriceDifference, calcLensPackPrice, calcSelectedExtrasPrice, getFrameData, getLensPacks, getPriceOfLensPack, getInitialLensPack } from './utils.js';

const isGlassesType = (input)=>input === 'frameOnly' || input === 'frameAndMonofocal' || input === 'frameAndMultifocal'
;
var ExtraTypes;
(function(ExtraTypes) {
    ExtraTypes['tinted-lenses-pack'] = 'tinted-lenses-pack';
    ExtraTypes['manufactured-lenses'] = 'manufactured-lenses';
    ExtraTypes['screen-protection-pack'] = 'screen-protection-pack';
    ExtraTypes['polarized-pack'] = 'polarized-pack';
    ExtraTypes['personalized-pack'] = 'personalized-pack';
    ExtraTypes['light-adaptive-pack'] = 'light-adaptive-pack';
    ExtraTypes['gradient-pack'] = 'gradient-pack';
})(ExtraTypes || (ExtraTypes = {}));
const isExtraType = (input)=>input in ExtraTypes
;
const Aside = ({ currency , price , prefix , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: className
    }, prefix, " ", /*#__PURE__*/ React.createElement(Price, {
        value: price,
        centAmount: true,
        currency: currency
    }))
;
class SSP extends React.Component {
    filterByAllowed(glasses, lensPack, extras) {
        // Get allowed extras based on the value and assign to newExtras
        let allowedExtras = this.props.lensExtrasResolver(glasses, lensPack, extras, this.opticalData);
        let newExtras = extras.filter((item)=>allowedExtras.indexOf(item) > -1
        );
        // With the newExtras, check allowedExtras again and filter it with the newExtras itself
        allowedExtras = this.props.lensExtrasResolver(glasses, lensPack, newExtras, this.opticalData);
        newExtras = extras.filter((item)=>allowedExtras.indexOf(item) > -1
        );
        return newExtras;
    }
    componentDidMount() {
        this.setState({
            isLoading: false
        });
        // Need to call onChange on mount to send initial values to parent on load
        this.props.onChange && this.props.onChange(this.state);
    }
    render() {
        const { monofocalLensPacks , multifocalLensPacks  } = this.opticalData;
        const { className , useIncludedLensPack , intl  } = this.props;
        const { selectedGlasses , selectedExtras  } = this.state;
        const selectedLensPack = this.state.selectedLensPack || 'bronze';
        const activePacks = selectedGlasses === 'frameAndMonofocal' ? monofocalLensPacks : multifocalLensPacks;
        this.extrasData = this.setExtrasData(selectedGlasses, selectedLensPack, selectedExtras);
        // Monofocal table will show only benefits that match this array and sort them accordingly
        const monoSorting = [
            'cbAntiBreakage',
            'cbAntiScratch',
            'cbAntiReflective',
            'cbHidrorrepelent',
            'cbReducedEyeFatigue',
            'cbThinnerLighter',
            'cbIdealForRimless',
            'cbUVprotection',
            'cbBetterContrast',
            'cbHDtechnology',
            'cbIndividualLenses', 
        ];
        // Multifocal table will show only benefits that match this array and sort them accordingly
        const multiSorting = [
            'visualField',
            'cbPerfectSight',
            'cbAntiBreakage',
            'cbAntiScratch',
            'cbAntiReflective',
            'cbHidrorrepelent',
            'cbThinnerLighter',
            'cbIdealForRimless',
            'cbUVprotection',
            'cbBetterContrast',
            'cbHDtechnology',
            'cbIndividualLenses', 
        ];
        const glassesItems = this.createGlassesItems();
        const sorting = selectedGlasses === 'frameAndMonofocal' ? monoSorting : multiSorting;
        const packsWithBenefits = findAndSortBenefits(activePacks, intl, sorting);
        const withVisualField = packsWithBenefits.filter((pack)=>pack.attributes
        ).map((pack)=>pack.attributes
        ).reduce((a, b)=>a.concat(b)
        , []).some((attr)=>attr.name === 'visualField'
        );
        const withVoucherValues = packsWithBenefits.length > 0 && packsWithBenefits.map((pack)=>{
            const voucherAttr = getAttrValue(pack, 'voucher');
            return isPlainObject(voucherAttr) ? getNestedProperty(voucherAttr, 'centAmount', 0) : 0;
        }).some((value)=>value > 0
        );
        const SSPclasses = classnames({
            ssp: true,
            'ssp--with-voucher': withVoucherValues,
            'ssp--with-visual-field': withVisualField,
            className
        });
        const selectedItems = this.state.selectedExtras.reduce((acc, name)=>{
            const extra = this.extrasData.find((e)=>e.value === name
            );
            return extra ? [
                ...acc,
                extra
            ] : acc;
        }, []);
        return /*#__PURE__*/ React.createElement("div", {
            className: SSPclasses
        }, /*#__PURE__*/ React.createElement("div", {
            className: "site-container ssp__container"
        }, this.state.isLoading ? /*#__PURE__*/ React.createElement(Loader, null) : /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "ssp__section-title"
        }, intl.getLabel('products.ssp.glasses.title')), /*#__PURE__*/ React.createElement(SelectionGroup, {
            items: glassesItems,
            selectedItem: this.state.selectedGlasses,
            onChange: this.handleGlassesTypeChange
        }), this.state.selectedGlasses !== 'frameOnly' && /*#__PURE__*/ React.createElement(React.Fragment, null, intl.getLabel('products.ssp.lenses.title') && /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "ssp__section-title"
        }, intl.getLabel('products.ssp.lenses.title')), /*#__PURE__*/ React.createElement(LensPacks, {
            packs: packsWithBenefits,
            selectedPack: this.state.selectedLensPack || 'bronze',
            selectedGlasses: this.state.selectedGlasses,
            onChange: this.handleLensPackChange,
            useIncludedLensPack: useIncludedLensPack
        }), this.extrasData.length > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, intl.getLabel('products.ssp.extras.title') && /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "ssp__section-title"
        }, intl.getLabel('products.ssp.extras.title')), /*#__PURE__*/ React.createElement(CheckboxList, {
            checkboxAlignment: "middle",
            className: "ssp__lens-extras",
            itemClassName: "ssp__lens-extras-item",
            idPrefix: "ssp-lens-extra__",
            items: this.extrasData,
            selectedItems: selectedItems,
            getItemIdentifier: (item)=>item.value
            ,
            isItemDisabled: (item)=>item.disabled
            ,
            renderItem: (item)=>/*#__PURE__*/ React.createElement("div", {
                    className: classnames('ssp-lens-extra__content', {
                        'ssp-lens-extra__content--disabled': item.disabled
                    })
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "ssp-lens-extra__main"
                }, /*#__PURE__*/ React.createElement("span", {
                    className: "ssp-lens-extra__title"
                }, item.title), /*#__PURE__*/ React.createElement("span", {
                    className: "ssp-lens-extra__text"
                }, item.text)), /*#__PURE__*/ React.createElement(Aside, {
                    prefix: "+",
                    price: item.price,
                    currency: intl.currency,
                    className: "ssp-lens-extra__price"
                }))
            ,
            onChange: this.handleExtrasChange
        }))))));
    }
    constructor(props){
        super(props);
        this.setExtrasData = (selectedGlasses, selectedLensPack, selectedExtras)=>{
            const { monofocalExtras , multifocalExtras  } = this.createExtrasItems(this.props.lensExtras);
            const allowedExtras = this.props.lensExtrasResolver(selectedGlasses, selectedLensPack, selectedExtras, this.opticalData);
            let extrasData = selectedGlasses === 'frameAndMonofocal' ? monofocalExtras : multifocalExtras;
            extrasData = extrasData.map((extra)=>{
                const enabled = allowedExtras && allowedExtras.indexOf(extra.value) > -1;
                const checked = enabled && selectedExtras && selectedExtras.indexOf(extra.value) > -1;
                return swcHelpers.objectSpread({}, extra, {
                    isChecked: checked,
                    disabled: !enabled
                });
            });
            return extrasData;
        };
        this.createExtrasItems = (data)=>{
            const { intl  } = this.props;
            const monofocalExtras = data.filter((item)=>isExtraType(item.slug.en) && item.masterVariant.prices && item.masterVariant.prices.length
            ).map((item)=>{
                // @ts-ignore, is checked in the filter above
                const price = item.masterVariant.prices[0].value.centAmount;
                const Aside1 = /*#__PURE__*/ React.createElement("div", null, "+ ", /*#__PURE__*/ React.createElement(Price, {
                    value: price,
                    centAmount: true,
                    currency: intl.currency
                }));
                return {
                    scope: 'extras',
                    title: getLocalizedValue(item.name, intl.locale),
                    text: item.description ? getLocalizedValue(item.description, intl.locale) : '',
                    value: item.slug.en,
                    aside: Aside1,
                    price,
                    disabled: false
                };
            });
            const multifocalExtras = data.filter((item)=>item.variants.length !== 0 && item.variants[0].prices && item.variants[0].prices.length && isExtraType(item.slug.en)
            ).map((item)=>{
                // @ts-ignore, is checked in the filter above
                const price = item.variants[0].prices[0].value.centAmount;
                return {
                    scope: 'extras',
                    title: item.name ? getLocalizedValue(item.name, intl.locale) : '',
                    text: item.description ? getLocalizedValue(item.description, intl.locale) : '',
                    value: item.slug.en,
                    aside: /*#__PURE__*/ React.createElement(Aside, {
                        prefix: "+",
                        price: price,
                        currency: intl.currency
                    }),
                    price,
                    disabled: false
                };
            });
            return {
                monofocalExtras,
                multifocalExtras
            };
        };
        this.createGlassesItems = ()=>{
            const { initialGlasses , useIncludedFrame , intl  } = this.props;
            const glassesTypes = [
                'frameOnly',
                'frameAndMonofocal',
                'frameAndMultifocal'
            ];
            const glassesItems = glassesTypes.filter((glassesType)=>{
                // Only NON-kids frames can have multifocal.
                return !(glassesType === 'frameAndMultifocal' && this.opticalData.isKidsGlasses);
            }).map((glassesType)=>{
                let price = calcGlassesPrice(this.opticalData, glassesType);
                let prefix = '';
                if (useIncludedFrame) {
                    price = calcGlassesPriceDifference(this.opticalData, glassesType, initialGlasses);
                    prefix = price < 0 ? '-' : price > 0 ? '+' : '';
                }
                return {
                    title: intl.getLabel(`products.ssp.glasses.${glassesType}.title`),
                    text: intl.getLabel(`products.ssp.glasses.${glassesType}.text`),
                    value: glassesType,
                    scope: 'glasses',
                    aside: useIncludedFrame && price === 0 ? /*#__PURE__*/ React.createElement("div", null, intl.getLabel('products.included')) : /*#__PURE__*/ React.createElement(Aside, {
                        prefix: prefix,
                        price: Math.abs(price),
                        currency: intl.currency
                    }),
                    disabled: false
                };
            });
            return glassesItems;
        };
        this.handleGlassesTypeChange = (type)=>{
            if (!isGlassesType(type)) {
                console.error(`Invalid glasses type: "${type}"`);
                return;
            }
            const { selectedLensPack , selectedExtras  } = this.state;
            this.extrasData = this.setExtrasData(type, selectedLensPack, selectedExtras);
            let newExtras = [];
            if (type === 'frameOnly') {
                newExtras = [];
            } else {
                newExtras = this.filterByAllowed(type, selectedLensPack, selectedExtras);
            }
            const newGlassesPrice = calcGlassesPrice(this.opticalData, type);
            const newLensPackPrice = calcLensPackPrice(this.opticalData, type, selectedLensPack, this.props.useIncludedLensPack);
            const newExtrasPrice = calcSelectedExtrasPrice(this.extrasData, newExtras);
            const totalPrice = newGlassesPrice + newLensPackPrice + newExtrasPrice;
            this.setState({
                selectedGlasses: type,
                selectedExtras: newExtras,
                glassesPrice: newGlassesPrice,
                lensPackPrice: newLensPackPrice,
                extrasPrice: newExtrasPrice,
                totalPrice
            }, ()=>{
                this.props.onChange && this.props.onChange(this.state);
            });
        };
        this.handleLensPackChange = (pack)=>{
            const { selectedGlasses , selectedExtras  } = this.state;
            this.extrasData = this.setExtrasData(selectedGlasses, pack, selectedExtras);
            const newExtras = this.filterByAllowed(selectedGlasses, pack, selectedExtras);
            const newGlassesPrice = calcGlassesPrice(this.opticalData, selectedGlasses);
            const newLensPackPrice = calcLensPackPrice(this.opticalData, selectedGlasses, pack, this.props.useIncludedLensPack);
            const newExtrasPrice = calcSelectedExtrasPrice(this.extrasData, newExtras);
            const totalPrice = newGlassesPrice + newLensPackPrice + newExtrasPrice;
            this.setState({
                selectedLensPack: pack,
                selectedExtras: newExtras,
                glassesPrice: newGlassesPrice,
                lensPackPrice: newLensPackPrice,
                extrasPrice: newExtrasPrice,
                totalPrice
            }, ()=>{
                this.props.onChange && this.props.onChange(this.state);
            });
        };
        this.handleExtrasChange = (items)=>{
            const extras = items.filter((item)=>isExtraType(item.value)
            );
            const values = extras.map((i)=>i.value
            );
            const { selectedGlasses , selectedLensPack  } = this.state;
            const newExtras = this.filterByAllowed(selectedGlasses, selectedLensPack, values);
            const newGlassesPrice = calcGlassesPrice(this.opticalData, selectedGlasses);
            const newLensPackPrice = calcLensPackPrice(this.opticalData, selectedGlasses, selectedLensPack, this.props.useIncludedLensPack);
            const newExtrasPrice = calcSelectedExtrasPrice(this.extrasData, newExtras);
            const totalPrice = newGlassesPrice + newLensPackPrice + newExtrasPrice;
            this.setState({
                selectedExtras: newExtras,
                glassesPrice: newGlassesPrice,
                lensPackPrice: newLensPackPrice,
                extrasPrice: newExtrasPrice,
                totalPrice
            }, ()=>{
                this.props.onChange && this.props.onChange(this.state);
            });
        };
        // all product data, dynamically created based on data provided by page
        const frameData = getFrameData(this.props.product);
        const lensPackData = getLensPacks(this.props.product, frameData.isKidsGlasses);
        this.opticalData = swcHelpers.objectSpread({}, frameData, lensPackData, {
            lensExtras: this.props.lensExtras,
            frameAndMonofocalPrice: 0,
            frameAndMultifocalPrice: 0
        });
        var ref;
        const includedLensPackPrice = this.opticalData.includedLensPack ? (ref = getPriceOfLensPack(this.opticalData.includedLensPack)) !== null && ref !== void 0 ? ref : 0 : 0;
        const initialPrice = props.useIncludedLensPack ? includedLensPackPrice + frameData.framePrice : frameData.framePrice;
        const frameAndMonofocalPrice = initialPrice;
        const initLensPackMultifocal = getInitialLensPack(this.opticalData.multifocalLensPacks);
        var ref1;
        const initialLensPackMultifocalPrice = props.useIncludedLensPack && initLensPackMultifocal ? (ref1 = getPriceOfLensPack(initLensPackMultifocal)) !== null && ref1 !== void 0 ? ref1 : 0 : 0;
        const frameAndMultifocalPrice = !this.opticalData.isKidsGlasses ? initialLensPackMultifocalPrice + initialPrice : 0;
        this.opticalData = swcHelpers.objectSpread({}, this.opticalData, {
            frameAndMonofocalPrice,
            frameAndMultifocalPrice
        });
        // initial options
        // Initial selected type of glasses
        const initialGlasses1 = this.props.initialGlasses || 'frameAndMonofocal';
        const initialLensPackType = this.props.initialLensPack || getInitialLensPack(this.opticalData.monofocalLensPacks).packType;
        // Initial extras start empty
        const initialExtras = this.props.initialExtras || [];
        // Set extrasData
        this.extrasData = this.setExtrasData(initialGlasses1, initialLensPackType, initialExtras);
        // Set all prices based on initial selections
        const newGlassesPrice1 = calcGlassesPrice(this.opticalData, initialGlasses1);
        const newLensPackPrice1 = calcLensPackPrice(this.opticalData, initialGlasses1, initialLensPackType, props.useIncludedLensPack);
        const newExtrasPrice1 = calcSelectedExtrasPrice(this.extrasData, initialExtras);
        const totalPrice1 = newGlassesPrice1 + newLensPackPrice1 + newExtrasPrice1;
        this.state = {
            isLoading: true,
            selectedGlasses: initialGlasses1,
            selectedLensPack: initialLensPackType,
            selectedExtras: initialExtras,
            glassesPrice: newGlassesPrice1,
            lensPackPrice: newLensPackPrice1,
            extrasPrice: newExtrasPrice1,
            totalPrice: totalPrice1
        };
    }
}
var SSP$1 = withIntl(SSP);

export { ExtraTypes, SSP, SSP$1 as default };
//# sourceMappingURL=SSP.js.map
